(ns hai.ui.components.collection.views.kanban
  "Kanban collection view: groups a collection's rows into declared, ordered
  lanes and renders them as a read-only board.

  Two parts:

  - `group-lanes` (HAI-001-01) - the pure grouping core, a function of the
    collection map. REPL-inspectable, no rendering.
  - `view` (HAI-001-02) - the board layout: an ordered column grid of lanes,
    each a header + count over a card list (or an empty-lane state), plus the
    collection's loading / error / no-items indicators. No pagination footer.

  Cards render through the collection's existing card-render hook
  (`:collection.view.card/render`), which expects a *form-wrapped* row (it reads
  the entity via `:form/current-value`). Unlike the editable table/cards views -
  which mint a real nested form per row via `form/collection` - this board is
  read-only, so it wraps each raw row in a lightweight `{:form/current-value row}`
  map rather than a live re-frame form. That is deliberate and kanban-native: a
  board of a few hundred cards has no edit state to carry, so paying for a form
  per card (and its setup effects) would be waste."
  (:require
   ["@dnd-kit/core" :refer [DndContext DragOverlay KeyboardSensor PointerSensor
                            closestCenter pointerWithin
                            useDraggable useDroppable useSensor useSensors]]
   ["react" :as react]
   [garden.stylesheet]
   [hai.entity.helpers :as entity]
   [hai.sharing.helper :as sharing]
   [hai.ui.components :as c]
   [hai.ui.components.collection.filters :as filters]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.components.collection.views.kanban-core :as kanban-core]
   [lambdaisland.ornament :as o]))

(def KanbanViewConfig
  "Dev-time config a collection declares to opt into this kanban view.

  Lives here, with the view that consumes it, but is *merged into the table
  schema* (`table/StaticTable` + `table/EditableTable`) so that
  `strip-extra-keys` keeps these keys through `decode-table-props` - otherwise
  they would be dropped as unknown extras before `group-lanes` (and the
  `settings/collection-view-options` view gating) ever saw them. All optional: a
  collection without kanban config simply omits them and keeps the default
  Table/Cards views."
  [:map
   [:collection.view.kanban/lanes
    {:optional true}
    :any]
   [:collection.view.kanban/bucket-fn
    {:optional true}
    :any]
   [:collection.view.kanban/group-by
    {:optional true}
    :any]
   ;; Injected write hook (HAI-003-03): a fn
   ;; `(fn [{:keys [row from-lane-id to-lane-id]}] ...)`. Its *presence* opts the
   ;; board into drag-to-move; absent, the board stays read-only. Declared here
   ;; so `strip-extra-keys` keeps it through `decode-table-props`.
   [:collection.view.kanban/on-card-move
    {:optional true}
    :any]
   ;; Card-shaped loading placeholder (hiccup) shown once per lane while the
   ;; first resolve is in flight. A board declares one so the skeleton matches
   ;; its own card; absent, the plain `lane-skeleton` bar is used.
   [:collection.view.kanban/skeleton
    {:optional true}
    :any]])

(def view-option
  "The view-switcher descriptor for the kanban view, in the same
  `{:value :title}` shape as `settings/view-options`. A kanban board's consumer
  puts this in its `:view-options` so the generic settings machinery offers
  Kanban without settings needing any kanban-specific knowledge."
  {:value :collection.view/kanban
   :title "Kanban"})

;; ---------------------------------------------------------------------------
;; Board layout (HAI-001-02)
;; ---------------------------------------------------------------------------

(o/defstyled board :div
  "The lane grid. The column count follows the number of declared lanes via the
  `--kanban-lanes` custom property (set inline by `view`) rather than an inline
  `grid-template-columns`, so the narrow-viewport collapse to a single column
  still wins (an inline `grid-template-columns` would out-specify the media
  query)."
  {:display               "grid"
   :grid-template-columns "repeat(var(--kanban-lanes, 3), minmax(0, 1fr))"
   :gap                   "20px"
   :align-items           "start"}
  [:at-media {:max-width "768px"}
   {:grid-template-columns "1fr"}])

;; A lane may declare `:collection.view.kanban.lane/background` — an `o/defprop`
;; token, wrapped into `var(…)` where it is applied, since a defprop read at
;; runtime is the variable's name and not its value. The padding and radius are
;; always here so a lane that declares none looks the same as one that does.
(o/defstyled lane :section
  :flex :flex-col
  {:gap "12px"
   :padding "8px"
   :border-radius "8px"})

(o/defstyled lane-header :div
  :flex :flex-row :items-center
  {:gap "8px"})

(o/defstyled lane-label :span
  {:font-weight "600"
   :color       c/--gray-12})

(o/defstyled lane-count :span
  :inline-flex :items-center :justify-center
  {:min-width     "20px"
   :padding       "0 6px"
   :border-radius "10px"
   :font-size     "12px"
   :background    c/--gray-3
   :color         c/--gray-11})

(o/defstyled lane-cards :div
  :flex :flex-col
  {:gap "8px"})

(o/defstyled lane-empty :div
  :flex :justify-center
  {:padding   "12px"
   :color     c/--gray-9
   :font-size "14px"})

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules skeleton-pulse
  (garden.stylesheet/at-keyframes
   :hai-kanban-skeleton-pulse
   ["0%"   {:background-color c/--gray-3}]
   ["50%"  {:background-color c/--gray-5}]
   ["100%" {:background-color c/--gray-3}]))

;; Card-shaped placeholder shown once per lane while the first resolve is in
;; flight (`cards` is nil), so a loading board reads as "loading" rather than a
;; column of `Nothing here.` empties. Generic fallback for a board that declares
;; no `:collection.view.kanban/skeleton` of its own; its height tracks a card.
(o/defstyled lane-skeleton :div
  {:height        "56px"
   :border-radius "10px"
   :background    c/--gray-3
   :animation     "hai-kanban-skeleton-pulse 1.4s ease-in-out infinite"})

(defn- board-skeleton
  "The loading placeholder for one lane: the collection's declared card-shaped
  `:collection.view.kanban/skeleton`, or the plain `lane-skeleton` bar."
  [collection]
  (or (:collection.view.kanban/skeleton collection) [lane-skeleton]))

(defn- render-card
  "Render one raw lane row through the collection's card-render hook.

  `cards/card-render` dispatches to `:collection.view.card/render` when present
  (the kanban case) and expects a form-wrapped row, so wrap the raw entity in a
  lightweight `{:form/current-value row}` map - see the ns docstring for why a
  full form is not minted here."
  [collection row]
  [cards/card-render collection {:form/current-value row}])

(defn- lane-contents
  "The inner markup shared by a read-only and a droppable lane: a header (label +
  count) over the card list, or the empty-lane state. `render-card-fn` is
  `(fn [i row] -> keyed card hiccup)`, so the two lane paths differ only in how a
  card is rendered (plain vs draggable) and in their container element, not in
  this structure. Returns a seq to `into` onto the caller's container.

  `cards` is `nil` while the collection is still loading (`group-lanes` carries
  the pre-resolve nil through) and `[]` once loaded but empty. A loading lane
  shows a single `skeleton` card (and no count) so the board reads as loading;
  only a `[]` lane shows the `Nothing here.` empty state, so the board fills in
  without flashing false empties."
  [label cards skeleton render-card-fn]
  (list
   [lane-header
    [lane-label label]
    (when (some? cards) [lane-count (count cards)])]
   (cond
     (seq cards)   (into [lane-cards] (map-indexed render-card-fn) cards)
     (some? cards) [lane-empty "Nothing here."]
     :else         [lane-cards skeleton])))

(defn- lane-column
  "One read-only board column: `lane-contents` under the ornament `lane`
  container. Empty lanes still render - the declared lane set is the fixed column
  layout."
  [collection {:collection.view.kanban.lane/keys [label cards background]}]
  (into [lane (when background {:style {:background-color (str "var(" background ")")}})]
        (lane-contents label cards (board-skeleton collection)
                       (fn [i row]
                         ^{:key (hash [i row])}
                         [render-card collection row]))))

;; ---------------------------------------------------------------------------
;; Drag-to-move spine (HAI-003-03)
;;
;; The functional drag core: draggable cards, droppable lanes, and an
;; `onDragEnd` that invokes the injected `:collection.view.kanban/on-card-move`
;; hook on a real lane change. Active *only* when that hook is declared;
;; otherwise the board renders through the read-only path above, untouched.
;;
;; The view stays write-agnostic and speaks only in lane-ids: it reverses the
;; @dnd-kit active/over ids back to a row and a to-lane-id and hands them to the
;; consumer, which owns the actual write. @dnd-kit's hooks (`useDraggable` /
;; `useDroppable` / `useSensors`) run inside reagent `:f>` function components,
;; the repo's established React-hook interop.
;;
;; Affordances + accessibility (HAI-003-06) layer on top of this core: a
;; `DragOverlay` lifted-card ghost that follows the pointer, a hovered-lane
;; highlight while a card is over a droppable (`isOver`), a muted in-place
;; placeholder at the source (so the board does not jump), and a `KeyboardSensor`
;; with @dnd-kit screen-reader announcements for a mouse-free drag.
;; ---------------------------------------------------------------------------

(defn- card-dom-id
  "Stable @dnd-kit draggable id for a lane row: the row entity's `*/uuid` value
  as a string (@dnd-kit ids are strings/numbers). Every draggable row is an
  entity carrying a uuid attr (decision B), so this is unique and render-stable."
  [row]
  (str (get row (entity/entity->uuid-attr row))))

(def ^:private card-control-selector
  "Controls that take Enter or Space themselves. `[contenteditable]` is matched
  by presence, minus the explicit `false`, since the attribute is equally valid
  written bare or as an empty string."
  (str "input, textarea, select, button, [role=button], "
       "[contenteditable]:not([contenteditable=\"false\"])"))

(defn- card-own-control?
  "True when a keystroke came from a control inside the card that needs Enter or
  Space itself — an inline editor's field, an urgency pill, a chip's ✕, the row
  title. @dnd-kit's keyboard sensor activates on both keys, so without this a
  pill's Enter would pick the card up on top of activating the pill.

  The match must be strictly inside the card: @dnd-kit puts `role=\"button\"` on
  the draggable root itself, and treating that as a control would exempt every
  keystroke and leave the card undraggable by keyboard."
  [event]
  (let [root    (.-currentTarget event)
        control (some-> (.-target event) (.closest card-control-selector))]
    (boolean (and control
                  (not (identical? control root))
                  (.contains root control)))))

(defn- drag-listeners
  "@dnd-kit's activator listeners, with the keyboard one deferring to any control
  the card contains."
  [draggable]
  (let [listeners (js->clj (.-listeners draggable) :keywordize-keys true)]
    (cond-> listeners
      (:onKeyDown listeners)
      (update :onKeyDown
              (fn [on-key-down]
                (fn [event]
                  (when-not (card-own-control? event)
                    (on-key-down event))))))))

(defn- draggable-card*
  "Function component (`:f>`, since `useDraggable` is a hook): wraps one card in
  a @dnd-kit draggable. The hook is always called - keeping hook order stable -
  and draggability is gated by `:disabled` (from `sharing/can-edit?`), so a
  non-editable card renders inert, with no drag listeners spread onto it. While a
  card is being dragged its source stays in place and dims to a muted placeholder
  (the `DragOverlay` ghost is what follows the pointer), so the board layout does
  not jump."
  [{:keys [collection row card-id disabled?]}]
  (let [d         (useDraggable #js {:id card-id :disabled disabled?})
        dragging? (.-isDragging d)
        style     (cond-> {}
                    (not disabled?) (assoc :cursor "grab")
                    dragging?       (assoc :opacity 0.4))]
    [:div (merge {:ref   (.-setNodeRef d)
                  :style style}
                 (when-not disabled?
                   (merge (js->clj (.-attributes d) :keywordize-keys true)
                          (drag-listeners d))))
     [render-card collection row]]))

(defn- droppable-lane*
  "Function component (`:f>`, since `useDroppable` is a hook): one board column
  made a drop target, including when empty. The lane's `<section>` carries the
  droppable ref directly - a plain element (not the ornament `lane`) so the ref
  is guaranteed to attach to the grid-item DOM node - mirroring `lane`'s flex
  column. While a card hovers over the lane (`isOver`) its background tints to
  signal the live drop target; the padding is always present so lighting up the
  tint does not shift the layout. Cards render through `draggable-card*`."
  [{:keys [collection lane-def]}]
  (let [{:collection.view.kanban.lane/keys [id label cards background]} lane-def
        d     (useDroppable #js {:id (str id)})
        over? (.-isOver d)]
    (into [:section {:ref   (.-setNodeRef d)
                     :style (cond-> {:display        "flex"
                                     :flex-direction "column"
                                     :gap            "12px"
                                     :padding        "8px"
                                     :border-radius  "8px"
                                     :transition     "background-color 120ms ease"}
                              background (assoc :background-color (str "var(" background ")"))
                              ;; The live drop target reads over its own wash.
                              over?      (assoc :background-color (str "var(" c/--gray-3 ")")))}]
          (lane-contents label cards (board-skeleton collection)
                         (fn [_ row]
                           (let [cid (card-dom-id row)]
                             ^{:key cid}
                             [:f> draggable-card* {:collection collection
                                                   :row        row
                                                   :card-id    cid
                                                   :disabled?  (not (sharing/can-edit? row))}]))))))

(defn- lane-index
  "Map of droppable dom-id -> lane-id, reversing an @dnd-kit `over.id` back to a
  declared lane-id."
  [lanes]
  (into {}
        (map (fn [{id :collection.view.kanban.lane/id}] [(str id) id]))
        lanes))

(defn- card-index
  "Map of draggable dom-id -> {:row :from-lane-id}, reversing an @dnd-kit
  `active.id` back to the dragged row and the lane it started in."
  [lanes]
  (into {}
        (for [{:collection.view.kanban.lane/keys [id cards]} lanes
              row cards]
          [(card-dom-id row) {:row row :from-lane-id id}])))

(defn- lane-collision
  "Collision detection for a lane board: prefer the lane under the pointer
  (`pointerWithin`), falling back to the lane whose centre is nearest
  (`closestCenter`) when the pointer is in the gutter between lanes. The default
  `rectIntersection` resolves by the dragged card's own rectangle, which on a
  board of wide cards biases toward the source lane; pointer-first collision
  makes the drop follow the cursor."
  [args]
  (let [hits (pointerWithin args)]
    (if (pos? (.-length hits))
      hits
      (closestCenter args))))

(defn- lane-label-index
  "Map of droppable dom-id -> lane label, for turning an @dnd-kit `over.id` into a
  human lane name in screen-reader announcements."
  [lanes]
  (into {}
        (map (fn [{id    :collection.view.kanban.lane/id
                   label :collection.view.kanban.lane/label}]
               [(str id) label]))
        lanes))

(defn- drag-announcements
  "A @dnd-kit screen-reader announcements for a lane drag, phrased in terms of the
  lane the card is over. `label-of` maps a droppable dom-id to its lane label; a
  drag outside any lane (nil `over`) is announced as such. Returns the JS
  announcements object @dnd-kit's accessibility layer expects."
  [label-of]
  #js {:onDragStart  (fn [_] "Picked up card.")
       :onDragOver   (fn [e]
                       (if-let [lane (some-> e .-over .-id label-of)]
                         (str "Card moved over " lane ".")
                         "Card is no longer over a lane."))
       :onDragEnd    (fn [e]
                       (if-let [lane (some-> e .-over .-id label-of)]
                         (str "Card dropped in " lane ".")
                         "Card dropped outside a lane."))
       :onDragCancel (fn [_] "Card drag cancelled.")})

(defn- lane-keyboard-coordinates
  "A @dnd-kit keyboard coordinate getter for a lane board: ArrowLeft/ArrowRight jump
  the dragged card a whole lane at a time - aligning its centre to the nearest
  lane in that direction - rather than nudging the default 25px, so a keyboard
  user crosses a wide lane in one press instead of ~seventeen. Reads the live
  droppable lane rectangles (`droppableRects`) and the dragged card's current
  rectangle (`collisionRect`) from the sensor context; returns the new translate
  coordinates, or `nil` (no move) for non-horizontal keys or when there is no
  lane further in that direction. Vertical arrows are a no-op - a board's lanes
  are horizontal."
  [event args]
  (let [code    (.-code event)
        ctx     (.-context args)
        current (.-currentCoordinates args)
        coll    (some-> ctx .-collisionRect)]
    (when (and coll (or (= code "ArrowRight") (= code "ArrowLeft")))
      (let [dir     (if (= code "ArrowRight") 1 -1)
            cur-cx  (+ (.-left coll) (/ (.-width coll) 2))
            centers (volatile! [])]
        (.forEach (.-droppableRects ctx)
                  (fn [rect _id]
                    (vswap! centers conj (+ (.-left rect) (/ (.-width rect) 2)))))
        (let [ahead (filter #(pos? (* dir (- % cur-cx))) @centers)]
          (when (seq ahead)
            (let [target (apply (if (pos? dir) min max) ahead)]
              #js {:x (+ (.-x current) (- target cur-cx))
                   :y (.-y current)})))))))

(defn- dnd-board*
  "Function component (`:f>`, since `useSensors`/`useSensor` are hooks): the drag
  board. Mounts a `DndContext` with a `PointerSensor` (~6px activation distance,
  so a plain click/tap on a nested control still fires and only a real drag lifts
  the card) and a `KeyboardSensor` (Space/Enter to pick up, Left/Right arrows to
  jump lanes via `lane-keyboard-coordinates`, Space/Enter to drop) around the
  droppable lanes, plus @dnd-kit screen-reader announcements. The active card id
  is held in React state so a `DragOverlay` can render the lifted-card ghost that
  follows the pointer above the lanes. On `onDragEnd`, reverses the active/over
  ids to a row and a to-lane-id, runs the pure `move-decision`, and calls
  `on-card-move` only on an actual lane change (a same-lane drop is a no-op)."
  [{:keys [collection lanes on-card-move]}]
  (let [[active-id set-active-id!] (react/useState nil)
        sensors    (useSensors
                    (useSensor PointerSensor #js {:activationConstraint #js {:distance 6}})
                    (useSensor KeyboardSensor #js {:coordinateGetter lane-keyboard-coordinates}))
        cards      (card-index lanes)
        lane-ids   (lane-index lanes)
        label-of   (lane-label-index lanes)
        active-row (:row (get cards active-id))
        on-start   (fn [e] (set-active-id! (some-> e .-active .-id)))
        on-cancel  (fn [_] (set-active-id! nil))
        on-end     (fn [e]
                     (set-active-id! nil)
                     (let [{:keys [row from-lane-id]} (get cards (some-> e .-active .-id))
                           to-lane-id                 (get lane-ids (some-> e .-over .-id))]
                       (when (and row to-lane-id)
                         (when-let [move (kanban-core/move-decision from-lane-id to-lane-id)]
                           (on-card-move (assoc move :row row))))))]
    [:> DndContext {:sensors            sensors
                    :collisionDetection lane-collision
                    :accessibility      {:announcements (drag-announcements label-of)}
                    :onDragStart        on-start
                    :onDragCancel       on-cancel
                    :onDragEnd          on-end}
     ;; `:align-items "stretch"` overrides `board`'s default `start` so each
     ;; droppable lane column fills the full board height; a card can then be
     ;; dropped anywhere in a column and the hovered-lane tint reads as a
     ;; full-height drop target.
     (into [board {:style {"--kanban-lanes" (count lanes)
                           :align-items     "stretch"}}]
           (map (fn [lane-def]
                  ^{:key (:collection.view.kanban.lane/id lane-def)}
                  [:f> droppable-lane* {:collection collection :lane-def lane-def}]))
           lanes)
     ;; `:drop-animation nil` disables @dnd-kit's default drop animation, which
     ;; transitions the overlay ghost back to the *source* card's position before
     ;; unmounting. On a cross-lane move that reads as the card flying back to
     ;; where it came from; instead the ghost vanishes at the release point while
     ;; `on-card-move`'s optimistic patch has already placed the real card in the
     ;; destination lane, so the card reads as landing where it was dropped.
     [:> DragOverlay {:drop-animation nil}
      (when active-row
        [render-card collection active-row])]]))

(defn view
  "Render `collection` as a kanban board - read-only by default, drag-to-move
  when the collection declares `:collection.view.kanban/on-card-move`.

  Groups rows into declared, ordered lanes (`group-lanes`) and lays them out as
  a column grid. When no `on-card-move` hook is present each column is a plain
  read-only `lane-column`; when the hook is present the grid is wrapped by
  `dnd-board*` so cards become draggable and lanes droppable, invoking the hook
  on a cross-lane drop (see the drag-spine section above). Reuses the cards
  view's error indicator, but renders *no* loading indicator: a board's load
  transition is carried by its lanes filling in, and the bespoke tasks board
  showed no board-level spinner - a whole-board spinner over the lanes on every
  resolve would be a parity regression (surfaced in the [HAI-001-07] review;
  mirrors [HAI-001-06]'s per-lane rationale for dropping the no-items banner). An
  empty board reads as per-lane `Nothing here.` cells alone - it renders no
  collection-level no-items banner, which would double the per-lane empties (a
  board's empty state is per-lane by design; see [HAI-001-06]). Renders no
  pagination footer (a board shows its whole page) and no search bar / controls -
  those are supplied by the collection wrapper's controls, as for the table and
  cards views.

  Honours a `:sibling` filters placement like the table and cards views do: a
  board has no in-view header row to host a filter widget, so a custom
  `:filters/render` panel (e.g. a facet sidebar) mounts here as a sibling of the
  lane grid, and the page's own layout grid positions it. Gated on the
  `:show-filters-table?` setting exactly as the table and cards views gate on
  their per-view flag, so a header funnel toggle can show/hide the sidebar (a
  board is single-view, so it reuses the table flag - the same flag the default
  settings `form-render` writes for a non-cards view). `filters/component` is a
  no-op when the collection declares no filter columns, so a plain board is
  untouched."
  [collection]
  (let [lanes        (kanban-core/group-lanes collection)
        on-card-move (:collection.view.kanban/on-card-move collection)]
    [:<>
     (when (and (= :sibling (:filters/placement collection))
                (-> collection :table/form :form/current-value :table/settings :show-filters-table?))
       [filters/component collection])
     (if on-card-move
       [:f> dnd-board* {:collection   collection
                        :lanes        lanes
                        :on-card-move on-card-move}]
       (into [board {:style {"--kanban-lanes" (count lanes)}}]
             (map (fn [lane-def]
                    ^{:key (:collection.view.kanban.lane/id lane-def)}
                    [lane-column collection lane-def]))
             lanes))
     [cards/error-indicator collection]]))
